import React, { useRef, useReducer, useEffect } from 'react';
import { Button } from '@cityelectricalfactors/ui-components';
import useOnScreen from 'hooks/useOnScreen';
import ApprovalsGroupSelectedUsers from './approvals_group_selected_users';
import ApprovalsGroupFormInputs from './approvals_group_form_inputs';
import ApprovalsGroupMessageModal from './approvals_group_message_modal';
import ApprovalsGroupSelectModal from './approvals_group_select_modal';
import useInititalLoadRequest from './useInitialLoadRequest';
import useSelectUserModal from './useSelectUserModal';
import useSubmitGroupRequest from './useSubmitGroupRequest';
import {
  requestErrors,
  inputPrompts,
  approvalsGroupText,
} from './approvals_group_text';
import {
  initialGroupState,
  approvalsGroupReducer,
  groupActions,
} from './approvals_group_reducer';

const ApprovalsGroupForm = ({
  editMode,
  initialLoadUrl,
  createGroupUrl,
  editGroupUrl,
  getUsersUrl,
  validateNameUrl,
  groupsUrl,
}) => {
  const [approvalGroupState, approvalGroupDispatch] = useReducer(
    approvalsGroupReducer,
    initialGroupState,
  );

  const {
    initialGroupName,
    nameInput,
    nameMaxLength,
    nameIsValid,
    nameErrMsg,
    descriptionInput,
    descriptionMaxLength,
    admins,
    adminsCount,
    adminsSelectionCount,
    adminsIsValid,
    adminsErrMsg,
    approvers,
    approversCount,
    approversSelectionCount,
    approversIsValid,
    users,
    usersCount,
    usersSelectionCount,
    usersIsValid,
    userModal,
    messageModal,
    inititalLoadRequest,
    submitGroupRequest,
    submitUserRequest,
    usersRequestParams,
    unloadListenerStatus,
  } = approvalGroupState;

  // --- INITIAL LOAD REQUEST --- //

  useInititalLoadRequest(
    editMode,
    initialLoadUrl,
    editGroupUrl,
    inititalLoadRequest,
    approvalGroupDispatch,
  );

  // ---- BEFORE UNLOAD ---- //
  // The following shows an alert if a user has entered
  // data into the form and tries to leave or refesh the page.

  const beforeUnloadListener = event => {
    event.preventDefault();
    event.returnValue = 'Changes that you made will not be saved.';
  };

  // ---- MODAL - SELECT USERS ----- //

  const { openUserModal, closeUserModal } = useSelectUserModal(
    admins,
    approvers,
    users,
    adminsCount,
    approversCount,
    usersCount,
    adminsSelectionCount,
    approversSelectionCount,
    usersSelectionCount,
    approvalsGroupText,
    submitUserRequest,
    getUsersUrl,
    usersRequestParams,
    approvalGroupDispatch,
    groupActions,
  );

  // ---- MODAL - REMOVE USERS ----- //

  const openMessageModal = (user, title) => {
    approvalGroupDispatch({
      type: groupActions.OPEN_MESSAGE_MODAL,
      payload: { user, title },
    });
  };

  const closeMessageModal = () => {
    approvalGroupDispatch({ type: groupActions.CLOSE_MESSAGE_MODAL });
  };

  const removeUserHandler = () => {
    approvalGroupDispatch({ type: groupActions.REMOVE_MESSAGE_MODAL });
  };

  // ---- CHECK ERRORS - ONSCREEN ---- //

  const nameErrRef = useRef();
  const nameErrVisible = useOnScreen(nameErrRef);
  const adminErrRef = useRef();
  const adminErrVisible = useOnScreen(adminErrRef);
  const formRef = useRef();

  // ------ SUBMIT GROUP REQUEST ------ //

  useSubmitGroupRequest(
    nameInput,
    descriptionInput,
    admins,
    approvers,
    users,
    editMode,
    createGroupUrl,
    editGroupUrl,
    submitGroupRequest,
    approvalGroupDispatch,
    beforeUnloadListener,
  );

  // -------- SUBMIT FORM -------- //

  const submitFormHandler = event => {
    event.preventDefault();

    const noGroupName = !nameInput.trim().length;

    // Prevent form submission if name or amdin are empty/invalid
    if (
      noGroupName ||
      !adminsSelectionCount ||
      !nameIsValid ||
      !adminsIsValid
    ) {
      // If group name is empty set error
      if (noGroupName)
        approvalGroupDispatch({
          type: groupActions.NAME_INVALID,
          payload: inputPrompts.uniqueName,
        });
      // If admin is not selected set error
      if (!adminsSelectionCount)
        approvalGroupDispatch({
          type: groupActions.ADMINS_INVALID,
          payload: inputPrompts.addGroupAdmin,
        });
      // Scroll group name into view
      if ((noGroupName || !nameIsValid) && !nameErrVisible) {
        formRef.current.scrollIntoView();
        return;
      }
      // Scoll admin selection into view
      if ((!adminsSelectionCount || !adminsIsValid) && !adminErrVisible) {
        formRef.current.scrollIntoView();
      }
      return;
    }

    // Enable HTTP Request to be sent
    approvalGroupDispatch({
      type: groupActions.UPDATE_GROUP_REQUEST,
      payload: true,
    });
  };

  const cancelFormHandler = () => {
    window.location.assign(groupsUrl);
  };

  // ---- BEFORE UNLOAD ---- //

  const inputsHaveValues =
    nameInput.length ||
    descriptionInput.length ||
    adminsSelectionCount ||
    approversSelectionCount ||
    usersSelectionCount;

  useEffect(() => {
    // The first condition prevents multiple event listeners being added
    // The second condition enables an event listener to be added onmount if removed with the unmount clean up function
    if (
      (inputsHaveValues && !unloadListenerStatus) ||
      (inputsHaveValues && unloadListenerStatus)
    ) {
      // Add listener if any of the fields
      window.addEventListener('beforeunload', beforeUnloadListener);
      // Update unloadListenerStatus state to prevent more event listeners been created
      approvalGroupDispatch({
        type: groupActions.SET_UNLOAD_STATUS,
        payload: true,
      });
    } else if (!inputsHaveValues && unloadListenerStatus) {
      // Remove listener if all fields are empty
      window.removeEventListener('beforeunload', beforeUnloadListener);
      // Update unloadListenerStatus state so that a new event listener will be added if the form state changes
      approvalGroupDispatch({
        type: groupActions.SET_UNLOAD_STATUS,
        payload: false,
      });
    }
    return () => {
      // Remove event listener when component unmounts
      window.removeEventListener('beforeunload', beforeUnloadListener);
    };
  }, [
    nameInput,
    adminsSelectionCount,
    approversSelectionCount,
    usersSelectionCount,
  ]);

  return (
    <form
      onSubmit={submitFormHandler}
      className="create-group-form mt-20"
      ref={formRef}>
      <h2 className="ff-r fw-700 fs-18 tc-grey-70 lh-1 mb-12">Group Details</h2>

      <ApprovalsGroupFormInputs
        initialGroupName={initialGroupName}
        nameInput={nameInput}
        nameIsValid={nameIsValid}
        nameErrMsg={nameErrMsg}
        nameErrRef={nameErrRef}
        descriptionInput={descriptionInput}
        approvalGroupDispatch={approvalGroupDispatch}
        validateNameUrl={validateNameUrl}
        nameMaxLength={nameMaxLength}
        descriptionMaxLength={descriptionMaxLength}
      />

      <hr
        className="create-group-divider mb-25"
        tabIndex="-1"
        aria-hidden="true"
      />

      <h2 className="ff-r fw-700 fs-18 tc-grey-70 lh-1 mb-20">Users</h2>

      <ApprovalsGroupSelectedUsers
        displayText={approvalsGroupText.admins}
        userCount={adminsCount}
        users={admins}
        isValid={adminsIsValid}
        errorMsg={adminsErrMsg}
        errorRef={adminErrRef}
        openUserModal={openUserModal}
        openMessageModal={openMessageModal}
        classes="mb-32"
      />

      <ApprovalsGroupSelectedUsers
        displayText={approvalsGroupText.approvers}
        userCount={approversCount}
        users={approvers}
        isValid={approversIsValid}
        errorMsg={requestErrors.error}
        openUserModal={openUserModal}
        openMessageModal={openMessageModal}
        classes="mb-32"
      />

      <ApprovalsGroupSelectedUsers
        displayText={approvalsGroupText.users}
        userCount={usersCount + approversCount}
        users={users}
        isValid={usersIsValid}
        errorMsg={requestErrors.error}
        openUserModal={openUserModal}
        openMessageModal={openMessageModal}
        classes="mb-60"
      />

      <ApprovalsGroupMessageModal
        options={messageModal}
        closeModal={closeMessageModal}
        removeUserHandler={removeUserHandler}
      />

      <ApprovalsGroupSelectModal
        options={userModal}
        closeModal={closeUserModal}
        approvalGroupDispatch={approvalGroupDispatch}
      />

      <hr className="create-group-divider" tabIndex="-1" aria-hidden="true" />

      <div className="fl ai-c">
        <Button type="submit" className="bg-cef-red tc-white ptb-12 plr-14">
          {editMode
            ? approvalsGroupText.btns.update
            : approvalsGroupText.btns.submit}
        </Button>
        <Button
          handleClick={cancelFormHandler}
          className="bg-transparent tc-grey-70 ml-16 p-0">
          {approvalsGroupText.btns.cancel}
        </Button>
      </div>
    </form>
  );
};

export default ApprovalsGroupForm;
